import { useState, useEffect, useMemo, useCallback, useRef, type KeyboardEvent, type ReactNode } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { api, ApiError } from '../services/api';
import { Link as RouterLink } from 'react-router-dom';
import type {
  BrandStrategy as BrandStrategyType,
  Envergure,
  GuardrailViolation,
  IndustryCode,
  MarqueProfile,
  StrategyItemTargets,
  StrategyScopedList,
} from '@traaker/shared';
import {
  BRAND_STRATEGY_LIMITS,
  INDUSTRY_CODES,
  STRATEGY_SCOPED_LISTS,
  effectiveFeatureFlags,
  industryLabels,
  isIndustryCode,
  resolveEntitlement,
  resolveLimits,
} from '@traaker/shared';
import { useStrategyTargets, type StrategyTargetOption } from '../components/brand/knowledge/useStrategyTargets';
import { itemTargetsForSave, reconcileMarqueProfiles } from '../components/brand/knowledge/scopedList';
import { useKnowledgeScopes, useReconcileDeadScopes } from '../components/brand/knowledge/useKnowledgeScopes';
import { targetColorHex } from '../utils/targetColors';
import { ScopedListManager } from '../components/brand/knowledge/ScopedListManager';
import KnowledgeAssetsView from '../components/brand/knowledge/KnowledgeAssetsView';
import PersonasManager from '../components/brand/knowledge/PersonasManager';
import { K, microLabel } from '../components/brand/knowledge/tokens';

// ── Static config ──

const ENVERGURE_LABEL_KEYS: Record<Envergure, string> = {
  'hyper-local': 'brandStrategy.envergure.hyperLocal',
  'multi-local': 'brandStrategy.envergure.multiLocal',
  'mono-country': 'brandStrategy.envergure.monoCountry',
  'multi-country': 'brandStrategy.envergure.multiCountry',
  global: 'brandStrategy.envergure.global',
};
const ENVERGURE_SHORT_KEYS: Record<Envergure, string> = {
  'hyper-local': 'brandStrategy.envergureShort.hyperLocal',
  'multi-local': 'brandStrategy.envergureShort.multiLocal',
  'mono-country': 'brandStrategy.envergureShort.monoCountry',
  'multi-country': 'brandStrategy.envergureShort.multiCountry',
  global: 'brandStrategy.envergureShort.global',
};

type Axis = 'narrative' | 'authority' | 'referral';
/** Axis hues of the prompt-studio mock (`.rail.*`). */
const AXIS_COLOR: Record<Axis, string> = { narrative: '#6D28D9', authority: '#0F766E', referral: '#BE185D' };

type TabKey = 'levers' | 'identity' | 'context' | 'assets';
const TABS: TabKey[] = ['levers', 'identity', 'context', 'assets'];

/** Marque switcher value for the shared (top-level) identity + market fields. */
const ALL_MARQUES = '__all__';
const CIBLES_MARQUE_HREF = '/geo-strategy?cibles=marque#cibles';

type LeverField = 'scenarios' | 'keywords' | 'decisionCriteria';
const LEVERS: { field: LeverField; axis: Axis; soon?: boolean }[] = [
  { field: 'scenarios', axis: 'referral' },
  { field: 'keywords', axis: 'authority' },
  { field: 'decisionCriteria', axis: 'narrative', soon: true },
];

/** Which tab owns a field — used to jump to the first guardrail error after a save. */
const FIELD_TAB: Record<string, TabKey> = {
  scenarios: 'levers',
  keywords: 'levers',
  decisionCriteria: 'levers',
  aliases: 'identity',
  industry: 'identity',
  clientPersonas: 'identity',
  businessModel: 'identity',
  geographicFocus: 'identity',
  marqueProfiles: 'identity',
  description: 'context',
  differentiators: 'context',
  customerQuestions: 'context',
  customerObjections: 'context',
  avoidTopics: 'context',
  envergure: 'context',
};

// ── Draft model ──

interface Draft {
  aliases: string[];
  industry: IndustryCode | '';
  industryLegacy: string;
  clientPersonas: string;
  businessModel: 'b2b' | 'b2c' | 'both' | '';
  geographicFocus: string;
  description: string;
  differentiators: string[];
  scenarios: string[];
  keywords: string[];
  decisionCriteria: string[];
  customerQuestions: string[];
  customerObjections: string[];
  avoidTopics: string[];
  envergure: Envergure | '';
  itemTargets: StrategyItemTargets;
  /** Per-marque market overrides, keyed by an own Cibles marque targetId. Only non-empty profiles. */
  marqueProfiles: Record<string, MarqueProfile>;
}
type ValueKey = Exclude<keyof Draft, 'itemTargets' | 'industryLegacy' | 'marqueProfiles'>;
const VALUE_KEYS: ValueKey[] = [
  'aliases', 'industry', 'clientPersonas', 'businessModel', 'geographicFocus', 'description',
  'differentiators', 'scenarios', 'keywords', 'decisionCriteria', 'customerQuestions',
  'customerObjections', 'avoidTopics', 'envergure',
];

/** Drop empty per-field maps so `{scenarios: {}}` and `{}` compare equal. */
function normalizeItemTargets(it: StrategyItemTargets | undefined): StrategyItemTargets {
  const out: StrategyItemTargets = {};
  for (const f of STRATEGY_SCOPED_LISTS) {
    const m = it?.[f];
    if (m && Object.keys(m).length > 0) out[f] = m;
  }
  return out;
}

/** Drop empty profiles / blank fields so a cleared override compares equal to none. */
function normalizeProfiles(p: Record<string, MarqueProfile> | undefined): Record<string, MarqueProfile> {
  return reconcileMarqueProfiles(p, new Set()) ?? {};
}

/** Key-order independent JSON for map comparison. */
function stable(v: unknown): string {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${stable(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

/** `itemTargets` is NOT read from the brand row: it comes from `useKnowledgeScopes`. */
function draftFrom(s: BrandStrategyType | undefined): Draft {
  return {
    aliases: s?.aliases || [],
    industry: isIndustryCode(s?.industry) ? s!.industry! : '',
    industryLegacy: s?.industryLegacy || (isIndustryCode(s?.industry) ? '' : (s?.industry ?? '')),
    clientPersonas: s?.clientPersonas || '',
    businessModel: s?.businessModel || '',
    geographicFocus: s?.geographicFocus || '',
    description: s?.description || '',
    differentiators: s?.differentiators || [],
    scenarios: s?.scenarios || [],
    keywords: s?.keywords || [],
    decisionCriteria: s?.decisionCriteria || [],
    customerQuestions: s?.customerQuestions || [],
    customerObjections: s?.customerObjections || [],
    avoidTopics: s?.avoidTopics || [],
    envergure: s?.envergure || '',
    itemTargets: {},
    marqueProfiles: normalizeProfiles(s?.marqueProfiles),
  };
}

/** Fields that differ from the loaded snapshot. A list whose only change is its scoping counts once. */
function changedFields(draft: Draft, base: Draft): string[] {
  const out: string[] = VALUE_KEYS.filter((k) => stable(draft[k]) !== stable(base[k]));
  const a = normalizeItemTargets(draft.itemTargets);
  for (const f of STRATEGY_SCOPED_LISTS) {
    if (!out.includes(f) && stable(a[f] ?? {}) !== stable(base.itemTargets[f] ?? {})) out.push(f);
  }
  if (stable(normalizeProfiles(draft.marqueProfiles)) !== stable(base.marqueProfiles)) out.push('marqueProfiles');
  return out;
}

// ── Page ──

export default function BrandStrategy() {
  const { t, i18n } = useTranslation();
  const industryLabelMap = industryLabels(i18n.language);
  const { activeBrand, refreshBrands } = useBrand();
  const { user, tenant } = useAuth();
  const canManage = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'member';
  // Personas (BrandPersona) are a Conversations asset: same gate as the sidebar's
  // Conversations item (flag + `conversation_personas` quota not 0).
  const showPersonas =
    !!tenant &&
    !!effectiveFeatureFlags(tenant.tier, tenant.settings?.featureFlags).conversationsEnabled &&
    resolveLimits(tenant.tier, tenant.settings?.quotaOverrides).conversation_personas !== 0;
  const brandPersonasLimit = tenant
    ? resolveEntitlement('brand_personas', tenant.tier, {
        override: (tenant.settings?.quotaOverrides as Record<string, number> | undefined)?.brand_personas,
      }).limit
    : undefined;

  // Item scoping lives in its own rows: loaded separately, merged into the baseline.
  const scopes = useKnowledgeScopes(activeBrand?.brandId);
  const scopesReady = scopes.state.status === 'ready' && scopes.state.brandId === activeBrand?.brandId;
  const loadedScopes = scopesReady && scopes.state.status === 'ready' ? scopes.state.itemTargets : undefined;
  const baseScopes = useMemo(() => normalizeItemTargets(loadedScopes), [loadedScopes]);
  const strategyBaseline = useMemo(() => draftFrom(activeBrand?.strategy), [activeBrand]);
  const baseline = useMemo(() => ({ ...strategyBaseline, itemTargets: baseScopes }), [strategyBaseline, baseScopes]);
  const [draft, setDraft] = useState<Draft>(baseline);
  const [tab, setTab] = useState<TabKey>('levers');
  // 'personas' is a rail entry, not a strategy lever: it never reads or writes the draft.
  // `#personas` is the deep link the conversation editor opens (« Modifier dans Base de connaissance »).
  const initialLever = (): LeverField | 'personas' => (window.location.hash === '#personas' ? 'personas' : 'scenarios');
  const [lever, setLever] = useState<LeverField | 'personas'>(initialLever);
  const [marque, setMarque] = useState<string>(ALL_MARQUES);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [reassigned, setReassigned] = useState(0);

  const hasStrategy = !!activeBrand?.strategy && Object.keys(activeBrand.strategy).length > 0;
  const [explainerOpen, setExplainerOpen] = useState(!hasStrategy);

  const targets = useStrategyTargets(
    activeBrand?.brandId,
    loadedScopes,
    Object.keys(activeBrand?.strategy?.marqueProfiles ?? {}),
  );
  useReconcileDeadScopes(activeBrand?.brandId, scopes, targets.missingIds, targets.loading, setReassigned);

  // A new brand row resets the whole draft; new scopes only replace the draft's scoping,
  // and only when the user has not edited it since the previous scopes (their edits win).
  const baseScopesRef = useRef(baseScopes);
  useEffect(() => {
    setDraft({ ...strategyBaseline, itemTargets: baseScopesRef.current });
    setError(null);
    setFieldErrors({});
  }, [strategyBaseline]);
  useEffect(() => {
    const prev = baseScopesRef.current;
    baseScopesRef.current = baseScopes;
    setDraft((d) => (stable(normalizeItemTargets(d.itemTargets)) === stable(prev) ? { ...d, itemTargets: baseScopes } : d));
  }, [baseScopes]);

  // Reset navigation only when the brand itself changes, not on every refresh.
  const brandId = activeBrand?.brandId;
  useEffect(() => {
    setTab('levers');
    setLever(initialLever());
    setMarque(ALL_MARQUES);
    setInfo(null);
    setReassigned(0);
    setExplainerOpen(!hasStrategy);
  }, [brandId]);

  const changes = useMemo(() => changedFields(draft, baseline), [draft, baseline]);
  const dirty = changes.length > 0;

  // Leaving the tab with unsaved edits: the browser's own confirmation.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const set = useCallback(<Key extends keyof Draft>(key: Key, value: Draft[Key]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);
  const setScopes = useCallback((field: StrategyScopedList, scopes: Record<string, string>) => {
    setDraft((d) => ({ ...d, itemTargets: { ...d.itemTargets, [field]: scopes } }));
  }, []);
  /** Set (or clear, with an empty value) one override of a marque; an emptied profile is removed. */
  const setMarqueField = useCallback(<F extends keyof MarqueProfile>(id: string, f: F, value: MarqueProfile[F] | '') => {
    setDraft((d) => {
      const next: MarqueProfile = { ...d.marqueProfiles[id] };
      if (value) next[f] = value as MarqueProfile[F];
      else delete next[f];
      const profiles = { ...d.marqueProfiles };
      if (Object.keys(next).length) profiles[id] = next;
      else delete profiles[id];
      return { ...d, marqueProfiles: profiles };
    });
  }, []);

  // Marque tabs: the own Cibles marques (root first), plus any stored profile whose marque
  // resolved by id outside the picker's pages. A confirmed-missing id gets no tab.
  const marqueTabs = useMemo(() => {
    const own = targets.options
      .filter((o) => o.category === 'marque')
      .sort((a, b) => (a.parentTargetId ? 1 : 0) - (b.parentTargetId ? 1 : 0) || a.label.localeCompare(b.label));
    const seen = new Set(own.map((o) => o.targetId));
    const stored = Object.keys(draft.marqueProfiles)
      .filter((id) => !seen.has(id))
      .map((id) => targets.resolve(id))
      .filter((o): o is StrategyTargetOption => !!o && o.category === 'marque');
    return [...own, ...stored];
  }, [targets, draft.marqueProfiles]);
  const activeMarque = marque === ALL_MARQUES ? undefined : marqueTabs.find((m) => m.targetId === marque);

  const handleCancel = () => {
    setDraft(baseline);
    setError(null);
    setFieldErrors({});
  };

  const handleSave = async () => {
    if (!activeBrand) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    setFieldErrors({});

    const reconciled = targets.reconcile(draft.itemTargets, {
      scenarios: draft.scenarios,
      keywords: draft.keywords,
      decisionCriteria: draft.decisionCriteria,
      differentiators: draft.differentiators,
      customerQuestions: draft.customerQuestions,
      customerObjections: draft.customerObjections,
      avoidTopics: draft.avoidTopics,
    });
    const sentScopes = itemTargetsForSave(scopesReady, reconciled);
    const payload: Partial<BrandStrategyType> = {
      aliases: draft.aliases,
      industry: draft.industry || undefined,
      industryLegacy: draft.industryLegacy || undefined,
      clientPersonas: draft.clientPersonas || undefined,
      businessModel: draft.businessModel || undefined,
      geographicFocus: draft.geographicFocus || undefined,
      description: draft.description || undefined,
      differentiators: draft.differentiators,
      scenarios: draft.scenarios,
      keywords: draft.keywords,
      decisionCriteria: draft.decisionCriteria,
      customerQuestions: draft.customerQuestions,
      customerObjections: draft.customerObjections,
      avoidTopics: draft.avoidTopics,
      envergure: draft.envergure || undefined,
      // `itemTargets` is the FULL desired scoping (`{}` clears every mapping). Omitted
      // (undefined, dropped by JSON) when the scopes never loaded: sending the empty draft
      // scoping would unassign every item. The server then only prunes removed texts.
      itemTargets: sentScopes,
      // Same rule: empty and confirmed-missing profiles dropped, `{}` when none is left.
      marqueProfiles: targets.reconcileMarqueProfiles(draft.marqueProfiles) ?? {},
      // targetLanguages is owned by LanguageZoneEditor (immediate save) and existingAssets
      // is no longer edited here: both are omitted so the persisted values stay untouched.
    };

    try {
      await api.patch(`/brands/${activeBrand.brandId}/strategy`, payload);
      if (sentScopes) scopes.setLoaded(normalizeItemTargets(sentScopes));
      await refreshBrands();
      scopes.reload();
      setInfo(t('brandStrategy.info.saved'));
    } catch (err: any) {
      if (err instanceof ApiError && err.details) {
        const violations = err.details as GuardrailViolation[];
        const errors: Record<string, string> = {};
        violations.forEach((v) => {
          // Map array/map field violations back to the parent field.
          let field = v.field.replace(/\[\d+\]$/, '');
          if (field.startsWith('itemTargets.')) field = field.split('.')[1] ?? field;
          else if (field.startsWith('marqueProfiles.')) field = 'marqueProfiles';
          errors[field] = v.message;
        });
        setFieldErrors(errors);
        setError(t('brandStrategy.error.contentNotAllowed'));
        const first = Object.keys(errors).find((f) => FIELD_TAB[f]);
        if (first) {
          setTab(FIELD_TAB[first]);
          if (LEVERS.some((l) => l.field === first)) setLever(first as LeverField);
        }
      } else {
        setError(err.message || t('brandStrategy.error.save'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!activeBrand) return;
    setValidating(true);
    setError(null);
    setInfo(null);
    try {
      await api.post(`/brands/${activeBrand.brandId}/strategy/validate`, {});
      await refreshBrands();
      setInfo(t('brandStrategy.info.validated'));
    } catch (err: any) {
      setError(err.message || t('brandStrategy.error.validate'));
    } finally {
      setValidating(false);
    }
  };

  if (!activeBrand) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          {t('brandStrategy.title')}
        </Typography>
        <Panel>
          <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            {t('brandStrategy.noBrand')}
          </Typography>
        </Panel>
      </Box>
    );
  }

  const readOnly = !canManage || saving;
  const brandStatus = activeBrand.strategyMeta?.status;
  const leversFilled = LEVERS.filter((l) => draft[l.field].length > 0).length;
  const tabHasError = (k: TabKey) => Object.keys(fieldErrors).some((f) => FIELD_TAB[f] === k);

  const tabMeta: Record<TabKey, { count?: number; warn?: boolean }> = {
    levers: { count: draft.scenarios.length + draft.keywords.length + draft.decisionCriteria.length, warn: leversFilled < LEVERS.length },
    identity: {},
    context: {
      count: draft.differentiators.length + draft.customerQuestions.length + draft.customerObjections.length + draft.avoidTopics.length,
    },
    assets: {},
  };

  const scoped = (field: StrategyScopedList, variant: 'full' | 'compact', placeholder?: string, emptyHint?: string) => (
    <ScopedListManager
      field={field}
      items={draft[field]}
      onItemsChange={(items: string[]) => set(field, items)}
      scopes={draft.itemTargets[field] ?? {}}
      onScopesChange={(s: Record<string, string>) => setScopes(field, s)}
      limits={BRAND_STRATEGY_LIMITS[field]}
      targets={targets}
      scopesUnavailable={scopesReady ? undefined : scopes.state.status === 'error' ? 'error' : 'loading'}
      readOnly={readOnly}
      placeholder={placeholder}
      error={fieldErrors[field]}
      emptyHint={emptyHint}
      variant={variant}
    />
  );

  return (
    <Box sx={{ pb: 2 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 40, height: 40, borderRadius: '10px', flexShrink: 0, display: 'grid', placeItems: 'center',
              color: 'primary.main', bgcolor: K.primarySoft,
            }}
          >
            <AutoStoriesOutlinedIcon sx={{ fontSize: 21 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontFamily: K.tight, fontSize: 22, fontWeight: 700, letterSpacing: '-0.022em', lineHeight: 1.2 }}>
                {t('brandStrategy.title')}
              </Typography>
              {brandStatus && (
                <Box data-tour="strategy-status" sx={{ display: 'inline-flex' }}>
                  <StatusPill status={brandStatus} />
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: 13, color: K.ink2, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Box component="span" sx={{ fontWeight: 600, color: K.ink }}>{activeBrand.name}</Box>
              {activeBrand.url && <Box component="span" sx={{ color: K.ink3 }}>{'  ·  '}{activeBrand.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</Box>}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} data-tour="strategy-actions">
          {canManage && hasStrategy && brandStatus !== 'validated' && (
            <Tooltip title={dirty ? t('brandStrategy.validateDirtyHint') : ''}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={validating ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                  onClick={handleValidate}
                  disabled={validating || dirty || saving}
                  sx={{ color: '#fff' }}
                >
                  {validating ? t('brandStrategy.action.validating') : t('brandStrategy.action.validate')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Explainer (compact, collapsible) ── */}
      <Panel sx={{ mb: 2 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setExplainerOpen((o) => !o)}
          sx={{
            width: '100%', border: 0, bgcolor: 'transparent', font: 'inherit', cursor: 'pointer', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.125,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('brandStrategy.whatIsIt.title')}</Typography>
          <Typography sx={{ fontSize: 12.5, color: K.ink2, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
            {t('brandStrategy.whatIsIt.subtitle')}
          </Typography>
          <ExpandMoreIcon sx={{ fontSize: 19, color: K.ink3, ml: 'auto', transform: explainerOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        </Box>
        <Collapse in={explainerOpen} timeout="auto" unmountOnExit>
          <Box sx={{ borderTop: `1px solid ${K.line}`, p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '150px 1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
            <Box sx={{ display: { xs: 'none', md: 'grid' }, placeItems: 'center', bgcolor: '#fff', borderRadius: '10px', border: `1px solid ${K.line}`, p: 1 }}>
              <img src="/assets/how-it-works/brand-knowledge.webp" alt="Brand Knowledge" style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', display: 'block' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: K.ink, mb: 1.25 }}>{t('brandStrategy.whatIsIt.intro')}</Typography>
              <Typography sx={{ ...microLabel, color: K.ink2, mb: 0.5 }}>{t('brandStrategy.whatIsIt.whyPrecision')}</Typography>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: K.ink2, whiteSpace: 'pre-wrap' }}>{t('brandStrategy.whatIsIt.precision')}</Typography>
            </Box>
            <Box>
              <Typography sx={{ ...microLabel, color: K.ink2, mb: 0.5 }}>{t('brandStrategy.whatIsIt.beforeEdit')}</Typography>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, color: K.ink2 }}>{t('brandStrategy.whatIsIt.validation')}</Typography>
            </Box>
          </Box>
        </Collapse>
      </Panel>

      {/* ── Callouts ── */}
      <Box sx={{ display: 'grid', gap: 1, mb: 2, '&:empty': { display: 'none' } }}>
        {!hasStrategy && !dirty && (
          <Callout tone="info">
            {t('brandStrategy.noStrategy')}{' '}
            {canManage ? t('brandStrategy.noStrategyStart') : t('brandStrategy.noStrategyNonAdmin')}
          </Callout>
        )}
        {error && <Callout tone="error" onClose={() => setError(null)}>{error}</Callout>}
        {info && <Callout tone="success" onClose={() => setInfo(null)}>{info}</Callout>}
        {scopes.state.status === 'error' && (
          <Callout tone="warn">
            {t('brandKnowledge.scope.scopesLoadError')}{' '}
            <Box
              component="button"
              type="button"
              onClick={scopes.reload}
              sx={{ border: 0, p: 0, bgcolor: 'transparent', font: 'inherit', fontWeight: 600, color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {t('brandKnowledge.scope.retry')}
            </Box>
          </Callout>
        )}
        {reassigned > 0 && (
          <Callout tone="info" onClose={() => setReassigned(0)}>{t('brandKnowledge.scope.reassigned', { count: reassigned })}</Callout>
        )}
        {hasStrategy && brandStatus === 'stale' && !dirty && <Callout tone="warn">{t('brandStrategy.alert.stale')}</Callout>}
        {hasStrategy && !brandStatus && <Callout tone="info">{t('brandStrategy.alert.notValidated')}</Callout>}
      </Box>

      {/* ── Section tabs ── */}
      <Box
        role="tablist"
        sx={{ display: 'flex', gap: 0.5, borderBottom: `1px solid ${K.line}`, mb: 2, overflowX: 'auto', scrollbarWidth: 'none' }}
      >
        {TABS.map((k) => {
          const on = k === tab;
          const meta = tabMeta[k];
          const hasErr = tabHasError(k);
          return (
            <Box
              key={k}
              component="button"
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setTab(k)}
              sx={{
                border: 0, bgcolor: 'transparent', font: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 0.875, px: 1.625, py: 1.125, mb: '-1px',
                fontSize: 13, fontWeight: 600, color: on ? K.primaryDark : K.ink2,
                borderBottom: '2px solid', borderColor: on ? K.primary : 'transparent',
                '&:hover': { color: on ? K.primaryDark : K.ink },
              }}
            >
              {t(`brandStrategy.tab.${k}`)}
              {meta.count !== undefined && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 11, fontWeight: 600, px: 0.75, borderRadius: 999, lineHeight: '17px', fontVariantNumeric: 'tabular-nums',
                    bgcolor: on ? K.primarySoft2 : K.chipBg, color: on ? K.primaryDark : K.ink2,
                  }}
                >
                  {meta.count}
                </Box>
              )}
              {(hasErr || meta.warn) && (
                <Tooltip title={hasErr ? t('brandStrategy.tabErrorHint') : t('brandStrategy.tabMissingHint')}>
                  <Box component="i" sx={{ width: 7, height: 7, borderRadius: 999, display: 'block', bgcolor: hasErr ? K.error : K.warning }} />
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Tab: Leviers de mesure ── */}
      {tab === 'levers' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '264px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
          <Panel sx={{ position: { md: 'sticky' }, top: { md: 80 } }}>
            <Box sx={{ px: 2, pt: 1.75, pb: 1.5, borderBottom: `1px solid ${K.line}` }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ ...microLabel, color: K.ink2 }}>{t('brandStrategy.levers.railTitle')}</Typography>
                <Typography sx={{ ml: 'auto', fontSize: 12, color: K.ink2, fontVariantNumeric: 'tabular-nums' }}>
                  <Box component="b" sx={{ color: K.ink, fontWeight: 700 }}>{leversFilled}</Box> / {LEVERS.length}
                </Typography>
              </Box>
              <Box sx={{ height: 5, borderRadius: 999, bgcolor: '#F1F1F3', overflow: 'hidden', mt: 1 }}>
                <Box sx={{ height: '100%', width: `${(leversFilled / LEVERS.length) * 100}%`, bgcolor: leversFilled === LEVERS.length ? 'success.main' : K.primary, transition: 'width .3s' }} />
              </Box>
            </Box>
            <Box sx={{ p: 0.75, display: 'grid', gap: 0.25 }}>
              {LEVERS.map((l) => {
                const on = l.field === lever;
                const n = draft[l.field].length;
                const err = !!fieldErrors[l.field];
                return (
                  <Box
                    key={l.field}
                    component="button"
                    type="button"
                    onClick={() => setLever(l.field)}
                    sx={{
                      border: 0, font: 'inherit', cursor: 'pointer', textAlign: 'left', width: '100%',
                      display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: '10px',
                      bgcolor: on ? K.primarySoft2 : 'transparent',
                      '&:hover': { bgcolor: on ? K.primarySoft2 : K.primarySoft },
                    }}
                  >
                    <Box component="i" sx={{ width: 7, height: 7, borderRadius: 999, bgcolor: AXIS_COLOR[l.axis], flexShrink: 0, display: 'block' }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: on ? 700 : 600, color: on ? K.primaryDark : K.ink, lineHeight: 1.3 }}>
                        {t(`brandStrategy.lever.${l.field}.title`)}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: K.ink3, fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>
                        {t('brandKnowledge.list.itemCount', { count: n })}
                      </Typography>
                    </Box>
                    {err ? (
                      <ErrorOutlineIcon sx={{ fontSize: 16, color: K.error }} />
                    ) : n > 0 ? (
                      <Box sx={{ width: 18, height: 18, borderRadius: 999, display: 'grid', placeItems: 'center', bgcolor: alpha('#10B981', 0.14), color: '#047857' }}>
                        <CheckIcon sx={{ fontSize: 12 }} />
                      </Box>
                    ) : (
                      <Tooltip title={t('brandStrategy.mandatory')}>
                        <Box component="i" sx={{ width: 7, height: 7, borderRadius: 999, bgcolor: K.warning, display: 'block', mr: 0.5 }} />
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ px: 2, pb: 1.75, pt: 0.5, fontSize: 11.5, lineHeight: 1.5, color: K.ink3 }}>
              {t('brandStrategy.levers.railHint')}
            </Typography>
            {showPersonas && (
              <Box sx={{ borderTop: `1px solid ${K.line}`, p: 0.75 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setLever('personas')}
                  sx={{
                    border: 0, font: 'inherit', cursor: 'pointer', textAlign: 'left', width: '100%',
                    display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: '10px',
                    bgcolor: lever === 'personas' ? K.primarySoft2 : 'transparent',
                    '&:hover': { bgcolor: lever === 'personas' ? K.primarySoft2 : K.primarySoft },
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: 16, color: lever === 'personas' ? K.primaryDark : K.ink2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: lever === 'personas' ? 700 : 600, color: lever === 'personas' ? K.primaryDark : K.ink, lineHeight: 1.3 }}>
                    {t('brandPersonas.railTitle')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Panel>

          {LEVERS.filter((l) => l.field === lever).map((l) => (
            <Panel key={l.field}>
              <Box sx={{ px: 2.25, pt: 2, pb: 1.75, borderBottom: `1px solid ${K.line}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontFamily: K.tight, fontSize: 17, fontWeight: 700, letterSpacing: '-0.018em' }}>
                    {t(`brandStrategy.lever.${l.field}.title`)}
                  </Typography>
                  <MandatoryPill done={draft[l.field].length > 0} />
                  <Box sx={{ flex: 1 }} />
                  <UsedBy>
                    <AxisPill axis={l.axis} />
                    {l.soon && <Pill>{t('brandStrategy.soon')}</Pill>}
                  </UsedBy>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: K.ink2, lineHeight: 1.55, mt: 0.75, maxWidth: 760 }}>
                  {t(`brandStrategy.lever.${l.field}.description`)}
                </Typography>
              </Box>
              <Box sx={{ p: 2.25 }}>
                {scoped(
                  l.field,
                  'full',
                  t(`brandStrategy.lever.${l.field}.placeholder`),
                  t(`brandStrategy.lever.${l.field}.empty`),
                )}
              </Box>
            </Panel>
          ))}

          {lever === 'personas' && showPersonas && activeBrand && (
            <Panel>
              <Box sx={{ px: 2.25, pt: 2, pb: 1.75, borderBottom: `1px solid ${K.line}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontFamily: K.tight, fontSize: 17, fontWeight: 700, letterSpacing: '-0.018em' }}>
                    {t('brandPersonas.title')}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <UsedBy>
                    <Pill>{t('brandPersonas.usedBy')}</Pill>
                  </UsedBy>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: K.ink2, lineHeight: 1.55, mt: 0.75, maxWidth: 760 }}>
                  {t('brandPersonas.description')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1, color: K.ink2 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15, mt: '2px', color: K.ink3 }} />
                  <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>{t('brandPersonas.independentNote')}</Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2.25 }}>
                <PersonasManager key={activeBrand.brandId} brandId={activeBrand.brandId} limit={brandPersonasLimit} readOnly={!canManage} />
              </Box>
            </Panel>
          )}
        </Box>
      )}

      {/* ── Tab: Identité & marché ── */}
      {tab === 'identity' && (
        <Box>
          {marqueTabs.length > 0 && (
            <MarqueSwitcher value={activeMarque ? marque : ALL_MARQUES} onChange={setMarque} marques={marqueTabs} />
          )}
          {activeMarque ? (
            <MarqueView
              key={activeMarque.targetId}
              marque={activeMarque}
              profile={draft.marqueProfiles[activeMarque.targetId]}
              shared={{ clientPersonas: draft.clientPersonas, businessModel: draft.businessModel, geographicFocus: draft.geographicFocus }}
              onChange={(f, v) => setMarqueField(activeMarque.targetId, f, v)}
              capReached={
                !draft.marqueProfiles[activeMarque.targetId] &&
                Object.keys(draft.marqueProfiles).length >= BRAND_STRATEGY_LIMITS.marqueProfiles
              }
              readOnly={readOnly}
              error={fieldErrors.marqueProfiles}
            />
          ) : (
            <Panel>
              <Section title={t('brandStrategy.section.identity')} purpose={t('brandStrategy.purpose.identity')}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Field label={t('brandStrategy.field.brandName')}>
                    <ReadValue>{activeBrand.name}</ReadValue>
                  </Field>
                  <Field label={t('brandStrategy.field.website')}>
                    <ReadValue>{activeBrand.url || '—'}</ReadValue>
                  </Field>
                  <Field label={t('brandStrategy.field.industry')}>
                    {readOnly ? (
                      <ReadValue>{draft.industry ? industryLabelMap[draft.industry] : (draft.industryLegacy || '—')}</ReadValue>
                    ) : (
                      <>
                        <Select
                          fullWidth
                          size="small"
                          displayEmpty
                          value={draft.industry}
                          onChange={(e) => set('industry', (e.target.value || '') as IndustryCode | '')}
                          renderValue={(v) =>
                            v ? industryLabelMap[v as IndustryCode] : <Box component="span" sx={{ color: K.ink3 }}>{t('brandStrategy.industryPlaceholder')}</Box>
                          }
                          sx={inputSx}
                        >
                          {INDUSTRY_CODES.map((code) => (
                            <MenuItem key={code} value={code}>{industryLabelMap[code]}</MenuItem>
                          ))}
                        </Select>
                        {draft.industryLegacy && !draft.industry && (
                          <Typography sx={{ fontSize: 11.5, color: 'warning.dark', mt: 0.5 }}>
                            {t('brandStrategy.industryLegacy', { legacy: draft.industryLegacy })}
                          </Typography>
                        )}
                      </>
                    )}
                  </Field>
                  <Field label={t('brandStrategy.field.aliases')} hint={t('brandStrategy.hint.aliases')}>
                    <AliasInput
                      items={draft.aliases}
                      onChange={(v) => set('aliases', v)}
                      readOnly={readOnly}
                      error={fieldErrors.aliases}
                    />
                  </Field>
                </Box>
              </Section>
              <Section title={t('brandStrategy.section.market')} purpose={t('brandStrategy.purpose.market')}>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Field label={t('brandStrategy.field.clientPersonas')}>
                    <LimitedText
                      value={draft.clientPersonas}
                      onChange={(v) => set('clientPersonas', v)}
                      max={BRAND_STRATEGY_LIMITS.clientPersonas}
                      placeholder={t('brandStrategy.placeholder.clientPersonas')}
                      error={fieldErrors.clientPersonas}
                      readOnly={readOnly}
                      minRows={3}
                    />
                  </Field>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 2.5 }}>
                    <Field label={t('brandStrategy.field.businessModel')}>
                      <Segmented
                        value={draft.businessModel}
                        onChange={(v) => set('businessModel', v as Draft['businessModel'])}
                        readOnly={readOnly}
                        options={[
                          { value: 'b2b', label: 'B2B' },
                          { value: 'b2c', label: 'B2C' },
                          { value: 'both', label: 'B2B & B2C' },
                        ]}
                        emptyLabel={t('brandStrategy.businessModel.undefined')}
                      />
                    </Field>
                    <Field label={t('brandStrategy.field.geographicFocus')}>
                      <LimitedText
                        value={draft.geographicFocus}
                        onChange={(v) => set('geographicFocus', v)}
                        max={BRAND_STRATEGY_LIMITS.geographicFocus}
                        placeholder={t('brandStrategy.placeholder.geographicFocus')}
                        error={fieldErrors.geographicFocus}
                        readOnly={readOnly}
                      />
                    </Field>
                  </Box>
                </Box>
              </Section>
            </Panel>
          )}
        </Box>
      )}

      {/* ── Tab: Contexte d'audit ── */}
      {tab === 'context' && (
        <Panel>
          <Section
            title={t('brandStrategy.section.valueProp')}
            purpose={t('brandStrategy.purpose.valueProp')}
            axes={['narrative']}
          >
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Field label={t('brandStrategy.field.description')}>
                <LimitedText
                  value={draft.description}
                  onChange={(v) => set('description', v)}
                  max={BRAND_STRATEGY_LIMITS.description}
                  placeholder={t('brandStrategy.placeholder.description')}
                  error={fieldErrors.description}
                  readOnly={readOnly}
                  minRows={3}
                />
              </Field>
              <Field label={t('brandStrategy.field.differentiators')}>
                {scoped('differentiators', 'compact', t('brandStrategy.placeholder.typeEnter'))}
              </Field>
            </Box>
          </Section>
          <Section
            title={t('brandStrategy.section.customerQuestions')}
            hint={t('brandStrategy.hint.customerQuestions')}
            purpose={t('brandStrategy.purpose.customerQuestions')}
            axes={['authority']}
          >
            {scoped('customerQuestions', 'compact', t('brandStrategy.placeholder.customerQuestions'))}
          </Section>
          <Section
            title={t('brandStrategy.section.customerObjections')}
            hint={t('brandStrategy.hint.customerObjections')}
            purpose={t('brandStrategy.purpose.customerObjections')}
            axes={['authority']}
          >
            {scoped('customerObjections', 'compact', t('brandStrategy.placeholder.customerObjections'))}
          </Section>
          <Section
            title={t('brandStrategy.section.avoidTopics')}
            hint={t('brandStrategy.field.avoidTopics')}
            note={t('brandStrategy.note.avoidTopicsImage')}
            purpose={t('brandStrategy.purpose.avoidTopics')}
            axes={['narrative']}
          >
            {scoped('avoidTopics', 'compact', t('brandStrategy.placeholder.avoidTopics'))}
          </Section>
          <Section
            title={t('brandStrategy.section.envergure')}
            hint={t('brandStrategy.hint.envergure')}
            purpose={t('brandStrategy.purpose.envergure')}
            axes={['authority', 'referral']}
          >
            <Segmented
              value={draft.envergure}
              onChange={(v) => set('envergure', v as Envergure | '')}
              readOnly={readOnly}
              options={(Object.keys(ENVERGURE_SHORT_KEYS) as Envergure[]).map((e) => ({
                value: e,
                label: t(ENVERGURE_SHORT_KEYS[e]),
                tooltip: t(ENVERGURE_LABEL_KEYS[e]),
              }))}
              emptyLabel={t('brandStrategy.envergure.undefined')}
            />
          </Section>
        </Panel>
      )}

      {/* ── Tab: Actifs de notoriété ── */}
      {tab === 'assets' && (
        <Box>
          <Typography sx={{ fontSize: 12.5, color: K.ink2, lineHeight: 1.55, mb: 1.5, maxWidth: 760 }}>
            {t('brandStrategy.assets.intro')}
          </Typography>
          <KnowledgeAssetsView brandId={activeBrand.brandId} />
        </Box>
      )}

      {/* ── Sticky save bar ── */}
      <Fade in={dirty && canManage} unmountOnExit>
        <Box sx={{ position: 'sticky', bottom: 16, zIndex: 20, mt: 3, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
          <Box
            role="status"
            sx={{
              pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 1.5, pl: 2, pr: 1, py: 0.875,
              bgcolor: K.ink, color: '#fff', borderRadius: '12px',
              boxShadow: '0 1px 2px rgba(0,0,0,.08), 0 16px 40px rgba(0,0,0,.24)',
            }}
          >
            {saving ? (
              <>
                <CircularProgress size={14} sx={{ color: '#C4B5FD' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('brandStrategy.savingOverlay')}</Typography>
              </>
            ) : (
              <>
                <Box component="i" sx={{ width: 7, height: 7, borderRadius: 999, bgcolor: K.warning, display: 'block' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {t('brandStrategy.saveBar.unsaved', { count: changes.length })}
                </Typography>
              </>
            )}
            <Box sx={{ width: 8 }} />
            <Button
              size="small"
              onClick={handleCancel}
              disabled={saving}
              sx={{ color: 'rgba(255,255,255,.72)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,.08)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,.3)' } }}
            >
              {t('brandStrategy.action.cancel')}
            </Button>
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving} sx={{ px: 2, '&.Mui-disabled': { bgcolor: alpha(K.primary, 0.5), color: 'rgba(255,255,255,.7)' } }}>
              {t('brandStrategy.action.save')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
}

// ── Building blocks (page-local) ──

const inputSx = {
  bgcolor: K.paper,
  borderRadius: '10px',
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: K.line },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,.2)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: K.primary, borderWidth: 1 },
  '&.Mui-focused': { boxShadow: K.focusRing },
} as const;

function Panel({ children, sx }: { children: ReactNode; sx?: object }) {
  return (
    <Box sx={{ bgcolor: K.paper, border: `1px solid ${K.line}`, borderRadius: '12px', overflow: 'hidden', ...sx }}>
      {children}
    </Box>
  );
}

/** One form section: a label column (title, what it is for, hint) beside its controls. */
function Section({
  title,
  hint,
  note,
  purpose,
  axes,
  children,
}: {
  title: string;
  hint?: string;
  note?: string;
  /** One sentence: what the field is used FOR (derived from its real consumers). */
  purpose?: string;
  /** The measurement axes whose prompts the field shapes. */
  axes?: Axis[];
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' }, gap: { xs: 1.5, md: 3 },
        px: 2.25, py: 2.25, '& + &': { borderTop: `1px solid ${K.line}` },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: K.tight, fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.012em' }}>{title}</Typography>
        {purpose && (
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: K.ink, lineHeight: 1.5, mt: 0.75 }}>{purpose}</Typography>
        )}
        {!!axes?.length && (
          <Box sx={{ display: 'flex', gap: 0.625, flexWrap: 'wrap', mt: 0.75 }}>
            {axes.map((a) => <AxisPill key={a} axis={a} />)}
          </Box>
        )}
        {hint && <Typography sx={{ fontSize: 12, color: K.ink2, lineHeight: 1.55, mt: 1 }}>{hint}</Typography>}
        {note && (
          <Typography sx={{ fontSize: 11.5, color: K.ink3, lineHeight: 1.5, mt: 0.75, display: 'flex', gap: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, mt: '2px', flexShrink: 0 }} />
            {note}
          </Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...microLabel, color: K.ink2, mb: 0.75 }}>{label}</Typography>
      {children}
      {hint && <Typography sx={{ fontSize: 11.5, color: K.ink3, mt: 0.5, lineHeight: 1.45 }}>{hint}</Typography>}
    </Box>
  );
}

function ReadValue({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: K.ink, minHeight: 36, display: 'flex', alignItems: 'center', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
      {children}
    </Typography>
  );
}

function UsedBy({ children, sx }: { children: ReactNode; sx?: object }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, flexWrap: 'wrap', ...sx }}>
      <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: K.ink3, mr: 0.25 }}>
        {t('brandStrategy.usedByShort')}
      </Typography>
      {children}
    </Box>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, borderRadius: 999, px: 1, py: '2px',
        fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: '16px',
        bgcolor: K.chipBg, color: '#3F3F46', border: '1px solid transparent',
      }}
    >
      {children}
    </Box>
  );
}

function AxisPill({ axis }: { axis: Axis }) {
  const { t } = useTranslation();
  const c = AXIS_COLOR[axis];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, borderRadius: 999, px: 1, py: '2px',
        fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: '16px',
        bgcolor: alpha(c, 0.1), color: c, border: '1px solid transparent',
      }}
    >
      <Box component="i" sx={{ width: 6, height: 6, borderRadius: 999, bgcolor: c, display: 'block' }} />
      {t(`brandStrategy.axis.${axis}`)}
    </Box>
  );
}

function MandatoryPill({ done }: { done: boolean }) {
  const { t } = useTranslation();
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, borderRadius: 999, px: 1, py: '2px',
        fontSize: 11, fontWeight: 700, letterSpacing: '.02em', lineHeight: '16px', whiteSpace: 'nowrap',
        bgcolor: done ? 'rgba(16,185,129,.12)' : 'rgba(245,158,11,.14)',
        color: done ? '#047857' : K.warningInk,
      }}
    >
      {done ? <CheckIcon sx={{ fontSize: 12 }} /> : <Box component="i" sx={{ width: 6, height: 6, borderRadius: 999, bgcolor: K.warning, display: 'block' }} />}
      {done ? t('brandStrategy.mandatoryDone') : t('brandStrategy.mandatory')}
    </Box>
  );
}

function StatusPill({ status }: { status: 'draft' | 'validated' | 'stale' }) {
  const { t } = useTranslation();
  const style = {
    draft: { bg: K.chipBg, fg: '#3F3F46', icon: null },
    validated: { bg: 'rgba(16,185,129,.12)', fg: '#047857', icon: <VerifiedIcon sx={{ fontSize: 13 }} /> },
    stale: { bg: 'rgba(245,158,11,.14)', fg: K.warningInk, icon: <WarningAmberRoundedIcon sx={{ fontSize: 13 }} /> },
  }[status];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, borderRadius: 999, px: 1.125, py: '3px',
        fontSize: 11.5, fontWeight: 600, lineHeight: '16px', bgcolor: style.bg, color: style.fg, whiteSpace: 'nowrap',
      }}
    >
      {style.icon}
      {t(`brandStrategy.status.${status}`)}
    </Box>
  );
}

function Callout({ tone, children, onClose }: { tone: 'info' | 'warn' | 'error' | 'success'; children: ReactNode; onClose?: () => void }) {
  const s = {
    info: { bg: 'rgba(14,165,233,.08)', fg: '#075985', bd: 'rgba(14,165,233,.2)', Icon: InfoOutlinedIcon },
    warn: { bg: 'rgba(245,158,11,.1)', fg: '#92400E', bd: 'rgba(245,158,11,.25)', Icon: WarningAmberRoundedIcon },
    error: { bg: 'rgba(239,68,68,.07)', fg: '#B91C1C', bd: 'rgba(239,68,68,.22)', Icon: ErrorOutlineIcon },
    success: { bg: 'rgba(16,185,129,.08)', fg: '#047857', bd: 'rgba(16,185,129,.22)', Icon: CheckCircleIcon },
  }[tone];
  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', borderRadius: '10px', px: 1.625, py: 1.25, fontSize: 12.5, lineHeight: 1.5, bgcolor: s.bg, color: s.fg, border: `1px solid ${s.bd}` }}>
      <s.Icon sx={{ fontSize: 17, mt: '1px', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      {onClose && (
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit', p: 0.25, mt: '-1px' }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Box>
  );
}

/** The mock's two-to-five segment switch. Clicking the active segment clears the value. */
function Segmented({
  value,
  onChange,
  options,
  readOnly,
  emptyLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; tooltip?: string }[];
  readOnly: boolean;
  emptyLabel: string;
}) {
  if (readOnly) {
    const o = options.find((x) => x.value === value);
    return <ReadValue>{o ? (o.tooltip ?? o.label) : <Box component="span" sx={{ color: K.ink3 }}>{emptyLabel}</Box>}</ReadValue>;
  }
  return (
    <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: '2px', bgcolor: K.segBg, borderRadius: '10px', p: '2px' }}>
      {options.map((o) => {
        const on = o.value === value;
        const btn = (
          <Box
            key={o.value}
            component="button"
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? '' : o.value)}
            sx={{
              border: 0, font: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
              fontSize: 12.5, fontWeight: 600, px: 1.5, py: 0.75, borderRadius: '8px',
              color: on ? K.ink : K.ink2, bgcolor: on ? K.paper : 'transparent',
              boxShadow: on ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
              '&:hover': { color: K.ink },
            }}
          >
            {o.label}
          </Box>
        );
        return o.tooltip ? <Tooltip key={o.value} title={o.tooltip}>{btn}</Tooltip> : btn;
      })}
    </Box>
  );
}

function LimitedText({
  value,
  onChange,
  max,
  placeholder,
  error,
  readOnly,
  minRows,
}: {
  value: string;
  onChange: (v: string) => void;
  max: number;
  placeholder?: string;
  error?: string;
  readOnly: boolean;
  minRows?: number;
}) {
  if (readOnly) return <ReadValue>{value || <Box component="span" sx={{ color: K.ink3 }}>—</Box>}</ReadValue>;
  const near = value.length > max * 0.9;
  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        multiline={!!minRows}
        minRows={minRows}
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, max))}
        placeholder={placeholder}
        error={!!error}
        inputProps={{ maxLength: max }}
        InputProps={{ sx: { ...inputSx, lineHeight: 1.55 } }}
      />
      <Box sx={{ display: 'flex', mt: 0.5, gap: 1 }}>
        {error && <Typography sx={{ fontSize: 11.5, color: K.errorInk }}>{error}</Typography>}
        <Typography sx={{ ml: 'auto', fontSize: 11, color: near ? 'warning.dark' : K.ink3, fontVariantNumeric: 'tabular-nums' }}>
          {value.length} / {max}
        </Typography>
      </Box>
    </Box>
  );
}

/** Aliases stay a plain token input: they only feed name matching, never scoped. */
function AliasInput({
  items,
  onChange,
  readOnly,
  error,
}: {
  items: string[];
  onChange: (v: string[]) => void;
  readOnly: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const cfg = BRAND_STRATEGY_LIMITS.aliases;
  const full = items.length >= cfg.maxItems;

  const add = () => {
    const v = input.trim();
    if (!v || full || v.length > cfg.maxCharsPerItem) return;
    if (!items.includes(v)) onChange([...items, v]);
    setInput('');
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !input && items.length) {
      onChange(items.slice(0, -1));
    }
  };

  const token = (item: string, i: number) => (
    <Box
      key={`${item}-${i}`}
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: K.chipBg, color: '#3F3F46', borderRadius: '6px', pl: 0.875, pr: readOnly ? 0.875 : 0.25, fontSize: 12.5, fontWeight: 500, lineHeight: '22px', whiteSpace: 'nowrap' }}
    >
      {item}
      {!readOnly && (
        <Box
          component="button"
          type="button"
          aria-label={t('brandStrategy.action.remove')}
          onClick={() => onChange(items.filter((_, idx) => idx !== i))}
          sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', color: K.ink3, fontSize: 11, px: 0.5, lineHeight: 1, '&:hover': { color: K.error } }}
        >
          ✕
        </Box>
      )}
    </Box>
  );

  if (readOnly) {
    return items.length ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 36, alignItems: 'center' }}>{items.map(token)}</Box>
    ) : (
      <ReadValue><Box component="span" sx={{ color: K.ink3 }}>—</Box></ReadValue>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, minHeight: 38, px: 0.75, py: 0.5,
          border: `1px solid ${error ? K.error : K.line}`, borderRadius: '10px', bgcolor: K.paper,
          '&:focus-within': { borderColor: K.primary, boxShadow: K.focusRing },
        }}
      >
        {items.map(token)}
        <Box
          component="input"
          value={input}
          disabled={full}
          maxLength={cfg.maxCharsPerItem}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value.slice(0, cfg.maxCharsPerItem))}
          onKeyDown={onKeyDown}
          onBlur={add}
          placeholder={items.length ? '' : t('brandStrategy.placeholder.typeEnter')}
          sx={{ flex: '1 0 120px', minWidth: 0, border: 0, outline: 0, bgcolor: 'transparent', font: 'inherit', fontSize: 13, py: 0.375, px: 0.5, color: K.ink }}
        />
      </Box>
      {(error || full) && (
        <Box sx={{ display: 'flex', mt: 0.5, gap: 1 }}>
          {error && <Typography sx={{ fontSize: 11.5, color: K.errorInk }}>{error}</Typography>}
          {full && (
            <Typography sx={{ ml: 'auto', fontSize: 11, color: K.ink3 }}>
              {t('brandKnowledge.list.full', { max: cfg.maxItems })}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/** « Toutes les marques » + one segment per own Cibles marque (KnowledgeAssetsView style). */
function MarqueSwitcher({
  value,
  onChange,
  marques,
}: {
  value: string;
  onChange: (v: string) => void;
  marques: StrategyTargetOption[];
}) {
  const { t } = useTranslation();
  const seg = (key: string, label: string, dot: ReactNode) => {
    const on = key === value;
    return (
      <Box
        key={key}
        component="button"
        type="button"
        aria-pressed={on}
        onClick={() => onChange(key)}
        sx={{
          border: 0, font: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.75,
          px: 1.5, py: 0.625, borderRadius: '8px', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
          color: on ? K.ink : K.ink2, bgcolor: on ? K.paper : 'transparent',
          boxShadow: on ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
          '&:hover': { color: K.ink },
        }}
      >
        {dot}
        {label}
      </Box>
    );
  };
  return (
    <Box
      role="group"
      aria-label={t('brandStrategy.marque.switcherAria')}
      sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.25, bgcolor: K.segBg, borderRadius: '10px', p: '2px', mb: 1.5 }}
    >
      {seg(ALL_MARQUES, t('brandStrategy.marque.all'), null)}
      {marques.map((m) =>
        seg(
          m.targetId,
          m.label,
          <Box component="i" sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: targetColorHex(m.color), display: 'block' }} />,
        ),
      )}
    </Box>
  );
}

type SharedMarket = Pick<Draft, 'clientPersonas' | 'businessModel' | 'geographicFocus'>;

/**
 * One marque of the « Identité & marché » tab: its identity as declared in Cibles (read
 * only) and the three market fields as OVERRIDES of the shared value. An empty override
 * inherits « Toutes les marques ».
 */
function MarqueView({
  marque,
  profile,
  shared,
  onChange,
  capReached,
  readOnly,
  error,
}: {
  marque: StrategyTargetOption;
  profile: MarqueProfile | undefined;
  shared: SharedMarket;
  onChange: <F extends keyof MarqueProfile>(f: F, v: MarqueProfile[F] | '') => void;
  capReached: boolean;
  readOnly: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  const ro = readOnly || capReached;
  const bmLabel: Record<string, string> = { b2b: 'B2B', b2c: 'B2C', both: 'B2B & B2C' };

  /** Under an override: « Revenir à la valeur commune » when set, the inherited value otherwise. */
  const inherit = (overridden: boolean, inherited: string, field: keyof MarqueProfile, showValue: boolean) => {
    if (overridden) {
      return ro ? null : (
        <Box
          component="button"
          type="button"
          onClick={() => onChange(field, '')}
          sx={{ border: 0, bgcolor: 'transparent', font: 'inherit', cursor: 'pointer', p: 0, mt: 0.5, fontSize: 11.5, fontWeight: 600, color: K.primaryDark, '&:hover': { textDecoration: 'underline' } }}
        >
          {t('brandStrategy.marque.reset')}
        </Box>
      );
    }
    if (!inherited) return null;
    return (
      <Typography sx={{ fontSize: 11.5, color: K.ink3, mt: 0.5, lineHeight: 1.45 }}>
        {showValue ? t('brandStrategy.marque.inheritedValue', { value: inherited }) : t('brandStrategy.marque.inherited')}
      </Typography>
    );
  };

  const chips = (values: string[] | undefined) =>
    values?.length ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 36, alignItems: 'center' }}>
        {values.map((v) => (
          <Box key={v} component="span" sx={{ bgcolor: K.chipBg, color: '#3F3F46', borderRadius: '6px', px: 0.875, fontSize: 12.5, fontWeight: 500, lineHeight: '22px' }}>
            {v}
          </Box>
        ))}
      </Box>
    ) : (
      <ReadValue><Box component="span" sx={{ color: K.ink3 }}>—</Box></ReadValue>
    );

  const personas = profile?.clientPersonas ?? '';
  const geo = profile?.geographicFocus ?? '';
  const bm = profile?.businessModel ?? '';

  return (
    <Panel>
      <Section title={t('brandStrategy.section.identity')} purpose={t('brandStrategy.purpose.identity')} hint={t('brandStrategy.marque.identityFromCibles')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Field label={t('brandStrategy.field.brandName')}>
            <ReadValue>
              <Box component="i" sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: targetColorHex(marque.color), display: 'inline-block', mr: 1 }} />
              {marque.label}
            </ReadValue>
          </Field>
          <Field label={t('brandStrategy.marque.domains')}>{chips(marque.domains)}</Field>
          <Field label={t('brandStrategy.field.aliases')}>{chips(marque.aliases)}</Field>
          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              component={RouterLink}
              to={CIBLES_MARQUE_HREF}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'divider', color: 'text.primary', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
            >
              {t('brandStrategy.assets.manage')}
            </Button>
          </Box>
        </Box>
      </Section>
      <Section title={t('brandStrategy.section.market')} purpose={t('brandStrategy.purpose.market')} hint={t('brandStrategy.marque.overrideHint')}>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {capReached && !readOnly && (
            <Callout tone="warn">{t('brandStrategy.marque.capReached', { max: BRAND_STRATEGY_LIMITS.marqueProfiles })}</Callout>
          )}
          {error && <Callout tone="error">{error}</Callout>}
          <Field label={t('brandStrategy.field.clientPersonas')}>
            <LimitedText
              value={personas}
              onChange={(v) => onChange('clientPersonas', v)}
              max={BRAND_STRATEGY_LIMITS.clientPersonas}
              placeholder={shared.clientPersonas || t('brandStrategy.placeholder.clientPersonas')}
              readOnly={ro}
              minRows={3}
            />
            {inherit(!!personas, shared.clientPersonas, 'clientPersonas', ro)}
          </Field>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 2.5 }}>
            <Field label={t('brandStrategy.field.businessModel')}>
              <Segmented
                value={bm}
                onChange={(v) => onChange('businessModel', v as MarqueProfile['businessModel'] | '')}
                readOnly={ro}
                options={[
                  { value: 'b2b', label: 'B2B' },
                  { value: 'b2c', label: 'B2C' },
                  { value: 'both', label: 'B2B & B2C' },
                ]}
                emptyLabel={t('brandStrategy.businessModel.undefined')}
              />
              {inherit(!!bm, shared.businessModel ? bmLabel[shared.businessModel] : '', 'businessModel', true)}
            </Field>
            <Field label={t('brandStrategy.field.geographicFocus')}>
              <LimitedText
                value={geo}
                onChange={(v) => onChange('geographicFocus', v)}
                max={BRAND_STRATEGY_LIMITS.geographicFocus}
                placeholder={shared.geographicFocus || t('brandStrategy.placeholder.geographicFocus')}
                readOnly={ro}
              />
              {inherit(!!geo, shared.geographicFocus, 'geographicFocus', ro)}
            </Field>
          </Box>
        </Box>
      </Section>
    </Panel>
  );
}

